import { walletApiSlice } from '../apiSlice';
import type { IWalletPagedResult } from './types';

/**
 * Scheduled coupon broadcasts — one-off or recurring sends of an existing coupon.
 *
 * <p>Kept out of `coupon.api.ts` because it is a different resource with its own lifecycle
 * (`active → paused → resumed → cancelled`) and its own cache tag. The coupon file stays about
 * coupons; this one is about when they go out.</p>
 *
 * <p>The delivery side is SignalR, not HTTP: the service pushes a `"CouponBroadcast"` event on
 * the `/hubs/coupons` hub when a schedule fires. Nothing here polls for that — a screen wanting
 * live broadcasts needs a hub connection as well as these endpoints.</p>
 */

export const RECURRENCE_PATTERN = {
	ONCE: 1,
	DAILY: 2,
	WEEKLY: 3,
	BI_WEEKLY: 4,
	MONTHLY: 5,
} as const;
export type IRecurrencePattern = (typeof RECURRENCE_PATTERN)[keyof typeof RECURRENCE_PATTERN];

export const RECURRENCE_PATTERN_LABEL: Record<IRecurrencePattern, string> = {
	[RECURRENCE_PATTERN.ONCE]: 'Once',
	[RECURRENCE_PATTERN.DAILY]: 'Daily',
	[RECURRENCE_PATTERN.WEEKLY]: 'Weekly',
	[RECURRENCE_PATTERN.BI_WEEKLY]: 'Every 2 weeks',
	[RECURRENCE_PATTERN.MONTHLY]: 'Monthly',
};

export const SCHEDULE_STATUS = { ACTIVE: 1, PAUSED: 2, COMPLETED: 3, CANCELLED: 4 } as const;
export type IScheduleStatus = (typeof SCHEDULE_STATUS)[keyof typeof SCHEDULE_STATUS];

export const SCHEDULE_STATUS_LABEL: Record<IScheduleStatus, string> = {
	[SCHEDULE_STATUS.ACTIVE]: 'Active',
	[SCHEDULE_STATUS.PAUSED]: 'Paused',
	[SCHEDULE_STATUS.COMPLETED]: 'Completed',
	[SCHEDULE_STATUS.CANCELLED]: 'Cancelled',
};

/**
 * Pause is legal only from `Active`, resume only from `Paused`; the service answers `400` with
 * `{ error: "Cannot pause schedule with status 'Completed'" }` otherwise.
 *
 * <p>Mirrored here so a UI can disable the affordance and say why, rather than offering a button
 * that fails after the click. The server stays the authority — a status it rejects should surface
 * its own message, not be pre-empted by a wider gate.</p>
 */
export const canPauseSchedule = (status?: IScheduleStatus | null): boolean => status === SCHEDULE_STATUS.ACTIVE;
export const canResumeSchedule = (status?: IScheduleStatus | null): boolean => status === SCHEDULE_STATUS.PAUSED;
export const canCancelSchedule = (status?: IScheduleStatus | null): boolean =>
	status === SCHEDULE_STATUS.ACTIVE || status === SCHEDULE_STATUS.PAUSED;

export interface ICouponScheduleResult {
	id: string;
	couponId: string;
	message: string;
	recurrencePattern: IRecurrencePattern;
	scheduledStartTime: string;
	endDate: string | null;
	/** Null once the schedule is completed or cancelled. */
	nextExecutionAt: string | null;
	lastExecutedAt: string | null;
	executionCount: number;
	status: IScheduleStatus;
	/** Empty means EVERY connected client, not "nobody" — see the create request. */
	targetUserIds: string[];
	createdAt: string;
}

export interface ICreateCouponScheduleRequest {
	couponId: string;
	message: string;
	recurrencePattern: IRecurrencePattern;
	scheduledStartTime: string;
	endDate?: string | null;
	/**
	 * <b>Empty or null broadcasts to EVERYONE.</b> An omitted list is not a no-op and not a
	 * validation error — it is the widest possible send, so a form that lets the field fall empty
	 * by accident mails the entire user base. Make the all-users case an explicit choice.
	 */
	targetUserIds?: string[] | null;
	/** wallet-service user GUID of the creating admin, not api.core's integer id. */
	createdBy: string;
}

export interface IUpdateCouponScheduleRequest {
	scheduleId: string;
	message: string;
	recurrencePattern: IRecurrencePattern;
	scheduledStartTime: string;
	endDate?: string | null;
	/** Same all-users rule as on create. */
	targetUserIds?: string[] | null;
}

export interface IListCouponSchedulesRequest {
	couponId?: string | null;
	status?: IScheduleStatus | null;
	recurrencePattern?: IRecurrencePattern | null;
	pageNumber?: number;
	pageSize?: number;
}

const couponScheduleApiSlice = walletApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		createCouponSchedule: builder.mutation<ICouponScheduleResult, ICreateCouponScheduleRequest>({
			query: ({ couponId, ...body }) => ({ url: `/coupons/${couponId}/schedules`, method: 'POST', body }),
			invalidatesTags: ['CouponSchedule'],
		}),

		/** Schedules for ONE coupon. Paged via query string, unlike the filtered list below. */
		getCouponSchedules: builder.query<
			IWalletPagedResult<ICouponScheduleResult>,
			{ couponId: string; pageNumber?: number; pageSize?: number }
		>({
			query: ({ couponId, pageNumber, pageSize }) => ({
				url: `/coupons/${couponId}/schedules`,
				params: { pageNumber, pageSize },
			}),
			providesTags: ['CouponSchedule'],
		}),

		/**
		 * Filtered search across every coupon's schedules.
		 *
		 * <p>A `query` despite the POST, for the same reason as the admin wallet search: it is a
		 * read, and only a query can carry `providesTags` — without which pause, resume and cancel
		 * leave the list showing the state it had before the click.</p>
		 */
		listCouponSchedules: builder.query<IWalletPagedResult<ICouponScheduleResult>, IListCouponSchedulesRequest>({
			query: (body) => ({ url: '/coupons/schedules/list', method: 'POST', body }),
			providesTags: ['CouponSchedule'],
		}),

		updateCouponSchedule: builder.mutation<ICouponScheduleResult, IUpdateCouponScheduleRequest>({
			query: ({ scheduleId, ...body }) => ({ url: `/coupons/schedules/${scheduleId}`, method: 'PUT', body }),
			invalidatesTags: ['CouponSchedule'],
		}),

		pauseCouponSchedule: builder.mutation<ICouponScheduleResult, string>({
			query: (scheduleId) => ({ url: `/coupons/schedules/${scheduleId}/pause`, method: 'POST' }),
			invalidatesTags: ['CouponSchedule'],
		}),

		/** Resuming recalculates `nextExecutionAt` server-side — read it back rather than deriving one. */
		resumeCouponSchedule: builder.mutation<ICouponScheduleResult, string>({
			query: (scheduleId) => ({ url: `/coupons/schedules/${scheduleId}/resume`, method: 'POST' }),
			invalidatesTags: ['CouponSchedule'],
		}),

		/**
		 * Cancel. Note the verb is DELETE but the schedule is NOT removed — it comes back with
		 * `status: 4` and a null `nextExecutionAt`, and keeps appearing in listings. A UI that
		 * optimistically drops the row is lying about what happened.
		 */
		cancelCouponSchedule: builder.mutation<ICouponScheduleResult, string>({
			query: (scheduleId) => ({ url: `/coupons/schedules/${scheduleId}`, method: 'DELETE' }),
			invalidatesTags: ['CouponSchedule'],
		}),

		/**
		 * Operator escape hatches: fire the recurring processors by hand instead of waiting for
		 * the timer. Both SEND REAL BROADCASTS to real users — they are not dry runs — so treat
		 * them as destructive and confirm before calling.
		 *
		 * Neither takes a body; the arg is typed `undefined` rather than `void` because
		 * `@typescript-eslint/no-invalid-void-type` rejects `void` as a generic argument. Call
		 * them as `trigger(undefined)`.
		 */
		triggerScheduledCouponBroadcasts: builder.mutation<unknown, undefined>({
			query: () => ({ url: '/coupons/broadcast/scheduled', method: 'POST' }),
			invalidatesTags: ['CouponSchedule'],
		}),
		processDueCouponSchedules: builder.mutation<unknown, undefined>({
			query: () => ({ url: '/coupons/schedules/process-due', method: 'POST' }),
			invalidatesTags: ['CouponSchedule'],
		}),
	}),
});

export const {
	useCreateCouponScheduleMutation,
	useGetCouponSchedulesQuery,
	useLazyGetCouponSchedulesQuery,
	useListCouponSchedulesQuery,
	useLazyListCouponSchedulesQuery,
	useUpdateCouponScheduleMutation,
	usePauseCouponScheduleMutation,
	useResumeCouponScheduleMutation,
	useCancelCouponScheduleMutation,
	useTriggerScheduledCouponBroadcastsMutation,
	useProcessDueCouponSchedulesMutation,
} = couponScheduleApiSlice;
